import React, { useState } from 'react';
import { Link } from 'react-router-dom';

function FloristFinder() {
  const [zip, setZip] = useState('');
  const [error, setError] = useState(null);

  const search = () => {
    const clean = zip.trim();
    if (!/^\d{5}$/.test(clean)) { setError('Enter a valid 5-digit zip code.'); return; }
    const url = `https://www.google.com/maps/search/florists+near+${clean}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div style={{ marginTop: '1rem', borderTop: '1px solid #f0e8df', paddingTop: '1rem' }}>
      <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#3d2b1f', marginBottom: '0.5rem' }}>
        Find florists near you
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          type="text"
          inputMode="numeric"
          maxLength={5}
          value={zip}
          onChange={e => { setZip(e.target.value.replace(/\D/g, '').slice(0, 5)); setError(null); }}
          onKeyDown={e => e.key === 'Enter' && search()}
          placeholder="Enter zip code"
          style={{ flex: 1, padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid #e8ddd0', fontSize: '0.9rem', fontFamily: 'inherit' }}
        />
        <button
          onClick={search}
          style={{ background: '#5D4037', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          Search
        </button>
      </div>
      {error && <div style={{ color: '#c62828', fontSize: '0.82rem', marginTop: '0.4rem' }}>{error}</div>}
    </div>
  );
}

const SECTIONS = [
  {
    key: "card",
    title: "Send a Card",
    icon: "✉️",
    summary: "A handwritten card is often the most remembered gesture.",
    tips: [],
    links: [
      {
        label: "Rifle Paper Co. Floral Card",
        url: "https://riflepaperco.com/products/floral-mother-s-day?variant=44569668124866",
        note: "Simple, can't-miss option. Order now so it arrives well before it needs to go out.",
        badge: "Our Pick",
      },
      {
        label: "Funny Duckling Card (Etsy)",
        url: "https://www.etsy.com/listing/1687408766/funny-duckling-mothers-day-card-snarky",
        note: "A lighter option if she has a sense of humor.",
        badge: "Funny",
      },
      {
        label: "Sunflower Stamps (USPS)",
        url: "https://store.usps.com/store/product/sunflowers-stamps-S_685804",
        note: "Don't have stamps? Order them today.",
        badge: "Stamps",
      },
      {
        label: "Felt — Handwritten Cards by Mail",
        url: "https://www.feltapp.com/",
        note: "Write with a stylus and they send it in your handwriting. But you should really write it yourself.",
        badge: "Service",
      },
    ],
  },
  {
    key: "flowers",
    title: "Order Flowers",
    icon: "🌸",
    summary: "Order now and schedule delivery a few days before so they're fresh and on time.",
    tips: [
      {
        text: "Wildflower bouquets feel personal and are harder to forget.",
        action: { label: "Jon's Pick: The Sunshower →", url: "https://urbanstems.com/products/the-sunshower" },
      },
      "Consider her birth month flower — a small detail that shows you thought about it.",
      {
        text: "A potted plant or herb she can keep lasts far longer than a bouquet.",
        action: { label: "Lula's Garden Heart Garden →", url: "https://www.lulasgarden.com/collections/mothers-day-gifts/products/mothers-day-heart-garden" },
      },
      "Most florists book up fast for Mother's Day — order early.",
    ],
  },
  {
    key: "reservation",
    title: "Make a Reservation",
    icon: "🍽️",
    summary: "Take her out so she doesn't have to cook. If she's far away, handle it for her.",
    tips: [
      "Book a restaurant she loves, or somewhere new she'd enjoy.",
      "If she's in another city, make a reservation there and send a gift card to cover it.",
      "Mother's Day is one of the busiest restaurant days of the year — don't wait.",
      "Even a picnic or home-cooked meal you take full responsibility for counts.",
    ],
  },
  {
    key: "gift",
    title: "Gift Ideas",
    icon: "🎁",
    summary: "A few reliably well-received gifts that work for most moms.",
    tips: [
      "Personalized jewelry — a piece with names, dates, or coordinates that matter to her.",
      "A spa day or massage — time to herself is often what she wants most.",
      "A photo book — gather photos from the past year and have them printed.",
      "A subscription she'd enjoy: flowers monthly, a cookbook club, an audiobook service.",
    ],
  },
];

function Section({ section, widget }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{
      border: '1px solid #e8ddd0',
      borderRadius: '10px',
      marginBottom: '0.75rem',
      background: '#fff',
      overflow: 'hidden',
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          padding: '1rem 1.25rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontSize: '1.4rem' }}>{section.icon}</span>
          <div>
            <div style={{ fontWeight: '600', fontSize: '1rem', color: '#3d2b1f' }}>{section.title}</div>
            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '2px' }}>{section.summary}</div>
          </div>
        </div>
        <span style={{ color: '#9a7c6e', fontSize: '1rem', flexShrink: 0, marginLeft: '0.5rem' }}>
          {open ? '▲' : '▼'}
        </span>
      </button>

      {open && (
        <div style={{ padding: '0 1.25rem 1.25rem', borderTop: '1px solid #f0e8df' }}>
          {section.tips.length > 0 && (
            <ul style={{ margin: '0.75rem 0 0.75rem', padding: '0 0 0 1.25rem', color: '#5a3e33', lineHeight: '1.7', fontSize: '0.9rem' }}>
              {section.tips.map((tip, i) => (
                <li key={i} style={{ marginBottom: typeof tip === 'object' ? '0.75rem' : '0.4rem' }}>
                  {typeof tip === 'object' ? (
                    <>
                      {tip.text}
                      {tip.action && (
                        <div style={{ marginTop: '0.35rem' }}>
                          <a
                            href={tip.action.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: 'inline-block', fontSize: '0.78rem', fontFamily: 'Arial, sans-serif', fontWeight: '600', color: '#5D4037', background: '#fdf0e6', border: '1px solid #e8c9a8', borderRadius: '6px', padding: '0.25rem 0.65rem', textDecoration: 'none' }}
                          >
                            {tip.action.label}
                          </a>
                        </div>
                      )}
                    </>
                  ) : tip}
                </li>
              ))}
            </ul>
          )}
          {section.links && section.links.length > 0 && (
            <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {section.links.map((link, i) => (
                <a
                  key={i}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'block', textDecoration: 'none', background: '#fdf6f0', border: '1px solid #e8ddd0', borderRadius: '8px', padding: '0.75rem 1rem' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
                    <span style={{ fontWeight: '600', fontSize: '0.9rem', color: '#3d2b1f' }}>{link.label}</span>
                    <span style={{ fontSize: '0.7rem', background: '#5D4037', color: '#fff', borderRadius: '20px', padding: '0.1rem 0.5rem', whiteSpace: 'nowrap' }}>{link.badge}</span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#9a7c6e', lineHeight: '1.4' }}>{link.note}</div>
                </a>
              ))}
            </div>
          )}
          {widget && widget}
        </div>
      )}
    </div>
  );
}

export default function MothersDay({ userId }) {
  const isLoggedIn = !!userId;

  return (
    <div style={{ minHeight: '100vh', background: '#fdf6f0', fontFamily: "Georgia, 'Times New Roman', serif" }}>
      <nav style={{
        background: '#fff',
        borderBottom: '1px solid #ede3d8',
        padding: '1rem 1.5rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <Link to="/" style={{ textDecoration: 'none', color: '#3d2b1f', fontWeight: '600', fontSize: '1.1rem', letterSpacing: '0.03em' }}>String</Link>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', fontSize: '0.9rem' }}>
          {isLoggedIn ? (
            <Link to="/upcoming" style={{ color: '#5D4037', textDecoration: 'none' }}>My Dashboard</Link>
          ) : (
            <>
              <Link to="/login" style={{ color: '#888', textDecoration: 'none' }}>Log In</Link>
              <Link to="/messaging_opt_in" style={{ background: '#5D4037', color: '#fff', padding: '0.4rem 1rem', borderRadius: '6px', textDecoration: 'none', fontSize: '0.85rem' }}>Sign Up</Link>
            </>
          )}
        </div>
      </nav>

      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '2rem 1.25rem 4rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🌷</div>
          <h1 style={{ fontSize: '2rem', color: '#3d2b1f', margin: '0 0 0.5rem', fontWeight: '600' }}>Mother's Day</h1>
          <p style={{ color: '#9a7c6e', fontSize: '1rem', margin: '0', lineHeight: '1.5' }}>
            May 10, 2026 &mdash; Four simple things to make it count.
          </p>
        </div>

        <a
          href="https://urbanstems.com/products/the-sunshower"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', display: 'block', marginBottom: '1.5rem' }}
        >
          <div style={{
            background: 'linear-gradient(135deg, #fff8f2 0%, #fdeee3 100%)',
            border: '2px solid #c8845a',
            borderRadius: '12px',
            padding: '1.25rem 1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}>
            <div style={{ fontSize: '2rem', flexShrink: 0 }}>🌻</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#c8845a', marginBottom: '0.2rem', fontFamily: 'Arial, sans-serif' }}>Jon's Pick</div>
              <div style={{ fontWeight: '700', fontSize: '1rem', color: '#3d2b1f', marginBottom: '0.15rem' }}>The Sunshower — UrbanStems</div>
              <div style={{ fontSize: '0.82rem', color: '#9a7c6e', lineHeight: '1.4', fontFamily: 'Arial, sans-serif' }}>One order, done. Ships in 1–2 days with same-week delivery options.</div>
            </div>
            <div style={{ flexShrink: 0 }}>
              <span style={{ background: '#5D4037', color: '#fff', borderRadius: '8px', padding: '0.45rem 0.9rem', fontSize: '0.82rem', fontFamily: 'Arial, sans-serif', fontWeight: '600', whiteSpace: 'nowrap' }}>
                Order Now →
              </span>
            </div>
          </div>
        </a>

        <div>
          {SECTIONS.map(section => (
            <Section
              key={section.key}
              section={section}
              widget={section.key === 'flowers' ? <FloristFinder /> : null}
            />
          ))}
        </div>

        {isLoggedIn ? (
          <div style={{
            marginTop: '2rem',
            background: '#fff',
            border: '1px solid #e8ddd0',
            borderRadius: '10px',
            padding: '1.25rem',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '1.1rem', marginBottom: '0.4rem' }}>✓ Track your progress</div>
            <p style={{ color: '#888', fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
              You have a personalized Mother's Day checklist in your dashboard.
            </p>
            <Link
              to="/upcoming"
              style={{
                display: 'inline-block',
                background: '#5D4037',
                color: '#fff',
                padding: '0.6rem 1.5rem',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: '0.9rem',
              }}
            >
              Go to My Checklist
            </Link>
          </div>
        ) : (
          <div style={{
            marginTop: '2rem',
            background: '#fff',
            border: '1px solid #e8ddd0',
            borderRadius: '10px',
            padding: '1.25rem',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '1.1rem', marginBottom: '0.4rem' }}>Get personalized reminders</div>
            <p style={{ color: '#888', fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
              Sign in to see your personalized Mother's Day checklist with your mom's name and saved gift ideas.
            </p>
            <Link
              to="/login"
              style={{
                display: 'inline-block',
                background: '#5D4037',
                color: '#fff',
                padding: '0.6rem 1.5rem',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: '0.9rem',
              }}
            >
              Log In to See Your Checklist
            </Link>
          </div>
        )}

        <p style={{ textAlign: 'center', color: '#c9a898', fontSize: '0.8rem', marginTop: '2.5rem' }}>
          String &mdash; stay on top of what's important.
        </p>
      </div>
    </div>
  );
}
